import React, { useEffect, useMemo, useRef, useState } from "react";
import { Avatar, CircularProgress } from "@mui/material";
import { useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import MaInput from "../../../../Components/UI/ma-input";
import MaButton from "../../../../Components/UI/ma-button";
import { SettingsSectionCard } from "./settings-section-card";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { PROFILE_EDIT } from "../../../../Utils/Mutations";
import { uploadFileToCloudinary } from "../../../../Utils/api";
import { useProfile } from "../../../../Utils/ProfileContext";

const ACCEPTED_IMAGE_TYPES = "image/png,image/jpeg,image/jpg,image/webp";
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

function SaveBtn({ onClick, disabled, loading }) {
  return (
    <MaButton
      variant="primary"
      title="Save Changes"
      onClick={onClick}
      disabled={disabled}
      loading={loading}
      sx={{
        bgcolor: "#2E1834",
        minHeight: "44px",
        px: 3,
        borderRadius: "12px",
        fontWeight: 600,
      }}
    />
  );
}

const formatPhoneForInput = (phone) => {
  if (!phone) return "";
  return phone.startsWith("+") ? phone : `+${phone}`;
};

const getCountryId = (country) => {
  if (!country) return null;
  if (typeof country === "string") return country;
  return country._id ?? country.id ?? null;
};

const getFavouriteCategoryIds = (categories) => {
  if (!Array.isArray(categories)) return null;
  return categories
    .map((item) => {
      if (typeof item === "string") return item;
      return item?._id ?? item?.id ?? null;
    })
    .filter(Boolean);
};

export function SettingsProfilePanel({ profile: profileProp }) {
  const { enqueueSnackbar } = useSnackbar();
  const { refetchProfile } = useProfile();
  const profileFromStore = useSelector((s) => s.user.profile);
  const profile = profileProp || profileFromStore;
  const fileInputRef = useRef(null);

  const [personal, setPersonal] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    bio: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [updateProfile, { loading: saving }] = useMutation(PROFILE_EDIT);

  useEffect(() => {
    if (!profile) return;
    setPersonal({
      firstName: profile.firstName || "",
      lastName: profile.lastName || "",
      email: profile.email || "",
      phone: formatPhoneForInput(profile.phone),
      bio: profile.bio || "",
    });
    setImageFile(null);
    setImagePreview(profile.imageUrl || null);
  }, [profile]);

  useEffect(() => {
    return () => {
      if (imagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const initials = useMemo(() => {
    const f = profile?.firstName?.[0] || "";
    const l = profile?.lastName?.[0] || "";
    return `${f}${l}`.toUpperCase() || "U";
  }, [profile]);

  const busy = saving || uploadingImage;

  const set = (key) => (eventOrValue) => {
    const next =
      typeof eventOrValue === "string"
        ? eventOrValue
        : eventOrValue?.target?.value ?? "";
    setPersonal((s) => ({ ...s, [key]: next }));
  };

  const handleImageSelect = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      enqueueSnackbar("Please choose a JPG or PNG image.", {
        variant: "warning",
      });
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      enqueueSnackbar("Image must be 10 MB or smaller.", {
        variant: "warning",
      });
      return;
    }

    if (imagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const uploadSelectedImage = async () => {
    if (!imageFile) return profile?.imageUrl || null;

    setUploadingImage(true);
    try {
      const response = await uploadFileToCloudinary(imageFile);
      const imageUrl = response?.data?.secure_url;
      if (!imageUrl) throw new Error("Image upload failed.");
      return imageUrl;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    if (!personal.firstName.trim()) {
      setSaveError("First name is required.");
      return;
    }
    if (!personal.lastName.trim()) {
      setSaveError("Last name is required.");
      return;
    }

    setSaveError(null);

    try {
      const countryId = getCountryId(profile?.country);
      if (!countryId) {
        setSaveError("Country is required to update your profile.");
        return;
      }

      const imageUrl = await uploadSelectedImage();

      await updateProfile({
        variables: {
          firstName: personal.firstName.trim(),
          lastName: personal.lastName.trim(),
          sex: profile?.sex ?? null,
          dateOfBirth: profile?.dateOfBirth ?? null,
          country: countryId,
          favouriteCategories: getFavouriteCategoryIds(
            profile?.favouriteCategories,
          ),
          imageUrl,
        },
      });

      setImageFile(null);
      setImagePreview(imageUrl);
      await refetchProfile?.();
      enqueueSnackbar("Profile updated successfully.", { variant: "success" });
    } catch (error) {
      setSaveError(error?.message ?? "Could not update profile.");
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <SettingsSectionCard
        title="Personal Information"
        subtitle="Update how buyers see you across Mamaket."
        footer={<SaveBtn onClick={handleSave} disabled={busy} loading={busy} />}
      >
        {saveError ? (
          <div className="flex justify-center">
            <span className="w-fit rounded-full border border-solid border-[#FF465C] bg-[#FF465C1a] px-3 py-1 text-center text-[13px] font-medium text-[#FF465C]">
              {saveError}
            </span>
          </div>
        ) : null}

        <div className="">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-x-4">
            <div className="relative shrink-0 self-start">
              <Avatar
                src={imagePreview || undefined}
                sx={{
                  width: { xs: 72, sm: 88 },
                  height: { xs: 72, sm: 88 },
                  bgcolor: "#2E1834",
                  fontSize: "2rem",
                  fontWeight: 700,
                }}
              >
                {initials}
              </Avatar>
              {uploadingImage ? (
                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
                  <CircularProgress size={28} sx={{ color: "#fff" }} />
                </div>
              ) : null}
            </div>

            <div className="w-full sm:w-auto [&>div]:w-full sm:[&>div]:w-auto">
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES}
                className="hidden"
                disabled={busy}
                onChange={handleImageSelect}
              />
              <MaButton
                sx={{ borderColor: "#EAEAEA" }}
                variant={"outlined"}
                title="Change Photo"
                disabled={busy}
                onClick={() => fileInputRef.current?.click()}
              />
              <p className="mt-1 text-center text-[12px] font-bold text-[#9D9FA4]">
                JPG or PNG · max 10MB
              </p>
            </div>
          </div>
          <div className="grid flex-1 grid-cols-1 gap-4 md:grid-cols-2 mt-5">
            <MaInput
              label="First Name"
              fullWidth
              value={personal.firstName}
              onChange={set("firstName")}
              borderColor="#E5E7EB"
              disabled={busy}
            />
            <MaInput
              label="Last Name"
              fullWidth
              value={personal.lastName}
              onChange={set("lastName")}
              borderColor="#E5E7EB"
              disabled={busy}
            />
            <MaInput
              label="Email address"
              fullWidth
              type="email"
              value={personal.email}
              onChange={set("email")}
              borderColor="#E5E7EB"
              disabled
            />
            <MaInput
              phone
              label="Phone number"
              fullWidth
              value={personal.phone}
              onChange={set("phone")}
              borderColor="#E5E7EB"
              disabled
            />
            <div className="md:col-span-2">
              <MaInput
                label="Bio"
                multiline
                minRows={4}
                fullWidth
                noHeight
                value={personal.bio}
                onChange={set("bio")}
                borderColor="#E5E7EB"
                disabled
              />
            </div>
            <div className="flex items-center gap-x-2">
              <IoIosInformationCircleOutline />
              <span className="text-[12px] text-[#626262]">
                Shown on your public seller profile
              </span>
            </div>
          </div>
        </div>
      </SettingsSectionCard>
    </div>
  );
}
